/**
 * Call-Off Detail Tab — left column of read-only field cards,
 * right column showing the audit history timeline.
 *
 * Mirrors the layout used by POLineDetailTab so the modal feels
 * consistent across PO and Call-Off surfaces.
 */
import { Calendar, User, Package, Truck, Warehouse, ArrowUpDown, FileText } from 'lucide-react';
import type {
  CallOff, CallOffTimelineEvent, CallOffEditHistoryEntry,
  EditHistoryItem, EditHistoryChangedItem,
} from '../../../lib/api';
import VerticalTimeline, { TimelineItem } from '../../purchasing/timeline/VerticalTimeline';
import { Progress } from '../../ui/progress';
import { Badge } from '../../ui/badge';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Pending', className: 'bg-blue-100 text-blue-700' },
  confirmed: { label: 'Confirmed', className: 'bg-green-100 text-green-700' },
  dispatched: { label: 'Dispatched', className: 'bg-orange-100 text-orange-700' },
  delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
};

const DESTINATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  warehouse: Warehouse,
  direct: Truck,
  mixed: ArrowUpDown,
};

interface CallOffDetailTabProps {
  callOff: CallOff;
  timeline: CallOffTimelineEvent[];
  editHistory: CallOffEditHistoryEntry[];
  formatDate: (date: string | Date) => string;
  formatDateTime: (date: string | Date) => string;
}

/**
 * Map a lifecycle event name to the timeline icon variant.
 */
function eventType(event: string): TimelineItem['type'] {
  const e = event.toLowerCase();
  if (e === 'created') return 'create';
  if (e === 'cancelled' || e === 'declined') return 'decline';
  if (e === 'confirmed' || e === 'delivered') return 'approve';
  return 'update';
}

/**
 * Convert call-off lifecycle events + line edit entries into a
 * single chronologically-ordered TimelineItem array.
 */
function buildAudit(
  timeline: CallOffTimelineEvent[],
  editHistory: CallOffEditHistoryEntry[],
): TimelineItem[] {
  const items: TimelineItem[] = [];
  let key = 0;

  for (const ev of timeline) {
    items.push({
      id: ++key,
      timestamp: ev.timestamp,
      user: ev.user || 'System',
      userRole: ev.company,
      action: ev.event.charAt(0).toUpperCase() + ev.event.slice(1).replace(/_/g, ' '),
      type: eventType(ev.event),
    });
  }

  for (const entry of editHistory) {
    const userLabel = entry.edited_by_name || 'System';
    const role = entry.edited_by_company_name;
    const reason = entry.delivery_name ? `Delivery: ${entry.delivery_name}` : undefined;

    for (const item of entry.items_added as EditHistoryItem[]) {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Added ${item.part_number}${item.requested_qty ? ` (qty ${item.requested_qty})` : ''}`,
        reason,
        type: 'create',
      });
    }
    for (const item of entry.items_removed as EditHistoryItem[]) {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Removed ${item.part_number}`,
        reason,
        type: 'decline',
      });
    }
    for (const item of entry.items_changed as EditHistoryChangedItem[]) {
      items.push({
        id: ++key,
        timestamp: entry.created_at,
        user: userLabel,
        userRole: role,
        action: `Changed quantity for ${item.part_number}`,
        field: item.field || 'quantity',
        oldValue: item.old_qty,
        newValue: item.new_qty,
        reason,
        type: 'update',
      });
    }
  }

  // newest first
  items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return items;
}

function ReadOnlyField({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${emphasis ? 'text-primary' : 'text-muted-foreground'}`}>
        {label}
      </label>
      <input
        type="text"
        value={value || '-'}
        readOnly
        className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 font-medium"
      />
    </div>
  );
}

function DateField({ label, value, formatDate }: { label: string; value: string | null; formatDate: (d: string | Date) => string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-muted-foreground mb-1">{label}</label>
      <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={value ? formatDate(value) : '-'}
          readOnly
          className="flex-1 bg-transparent border-none outline-none font-medium"
        />
      </div>
    </div>
  );
}

export default function CallOffDetailTab({
  callOff, timeline, editHistory, formatDate, formatDateTime: _formatDateTime,
}: CallOffDetailTabProps) {
  const auditItems = buildAudit(timeline, editHistory);
  const fillPercent = parseFloat(callOff.fill_percent) || 0;
  const statusConf = STATUS_LABELS[callOff.detail_status];
  const DestIcon = DESTINATION_ICONS[callOff.destination_type];
  const isMixed = callOff.destination_type === 'mixed';
  const destLabel = isMixed
    ? (callOff.destination_name?.replace(/^Mixed:\s*/, '') || 'Mixed Delivery')
    : (callOff.destination_name || callOff.destination_type);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left column — fields */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Reference + Status row */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Reference" value={callOff.reference} emphasis />
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
              <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2 h-[42px]">
                {statusConf && (
                  <Badge variant="secondary" className={statusConf.className}>{statusConf.label}</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Customer" value={callOff.customer_name} />
            <ReadOnlyField label="Supplier" value={callOff.supplier_name} />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <DateField label="Pickup Date" value={callOff.pickup_date} formatDate={formatDate} />
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Destination</label>
              <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2">
                {DestIcon && <DestIcon className="h-4 w-4 text-muted-foreground" />}
                <span className={`font-medium ${isMixed ? 'text-purple-700' : ''}`}>{destLabel || '-'}</span>
              </div>
            </div>
          </div>

          {/* Fill % */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Load Fill</label>
            <div className="flex items-center gap-3 border border-border rounded-lg bg-muted/30 px-3 py-2">
              <Progress value={Math.min(fillPercent, 100)} className="h-2 flex-1" />
              <span className={`text-sm font-bold whitespace-nowrap ${fillPercent > 90 ? 'text-red-600' : fillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {callOff.fill_percent}%
              </span>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-3">
            <ReadOnlyField label="Total Items" value={String(callOff.total_items)} />
            <ReadOnlyField label="Total Requested" value={callOff.total_requested_qty} />
            <ReadOnlyField label="Total Confirmed" value={callOff.total_confirmed_qty} />
          </div>

          {/* Lifecycle audit row — created / sent / confirmed / dispatched / delivered */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Created By" value={callOff.created_by_name} />
            <DateField label="Created At" value={callOff.created_at} formatDate={formatDate} />
          </div>
          {callOff.sent_at && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Sent By" value={callOff.sent_by_name} />
              <DateField label="Sent At" value={callOff.sent_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.confirmed_at && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Confirmed By" value={callOff.confirmed_by_name} />
              <DateField label="Confirmed At" value={callOff.confirmed_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.dispatched_at && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Dispatched By" value={callOff.dispatched_by_name} />
              <DateField label="Dispatched At" value={callOff.dispatched_at} formatDate={formatDate} />
            </div>
          )}
          {callOff.delivered_at && (
            <DateField label="Delivered At" value={callOff.delivered_at} formatDate={formatDate} />
          )}
          {callOff.cancelled_at && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Cancelled By" value={callOff.cancelled_by_name} />
                <DateField label="Cancelled At" value={callOff.cancelled_at} formatDate={formatDate} />
              </div>
              {callOff.cancellation_reason && (
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">Cancellation Reason</label>
                  <div className="border border-red-200 rounded-lg bg-red-50 px-3 py-2 font-medium text-red-900 whitespace-pre-wrap">
                    {callOff.cancellation_reason}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notes */}
          {callOff.notes && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                Notes
              </label>
              <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted/30 text-sm whitespace-pre-wrap">
                {callOff.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right column — audit history */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold">Audit History</h3>
        </div>
        <div className="max-h-[600px] overflow-y-auto pr-2">
          {auditItems.length > 0 ? (
            <VerticalTimeline items={auditItems} />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No audit history yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
